import React, {useState} from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    FormControlLabel,
    Switch,
    Button,
    Alert,
    CircularProgress,
    Divider,
    Chip,
    Stack,
    Backdrop, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {School, Person} from '@mui/icons-material';
import {useLogto} from "@logto/react";
import {usePostHog} from 'posthog-js/react';

const UserSetup = ({
                       accessToken,
                       user,
                       onUserUpdated,
                       showGreeting = true,
                       title = "Welcome to the Platform!",
                       submitButtonText = "Complete Setup",
                       isOnboarding = false
                   }) => {
    const [username, setUsername] = useState(user?.username || '');
    const [isTeacher, setIsTeacher] = useState(user?.is_teacher || false);
    const [posthogTracking, setPosthogTracking] = useState(user?.posthog_tracking_enabled || false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [willReauthenticate, setWillReauthenticate] = useState(false);

    const {clearAllTokens, signIn} = useLogto();
    const posthog = usePostHog();

    // Store original values to detect changes
    const originalUsername = user?.username || '';
    const originalIsTeacher = user?.is_teacher || false;

    // Check if user has admin role
    const isAdmin = user?.roles?.includes('admin') || user?.roles?.includes('administrator') || user?.is_administrator;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!username.trim()) {
            setError('Please enter a username');
            return;
        }

        // Validate username format
        if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(username)) {
            setError('Username must start with a letter or underscore, and can only contain letters, numbers, and underscores.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Create API client
            const apiClient = {
                put: async (url, data) => {
                    const response = await fetch(`${import.meta.env.VITE_API_URL}${url}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${accessToken}`
                        },
                        body: JSON.stringify(data)
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.detail || 'Failed to update profile');
                    }

                    return response.json();
                }
            };

            const userId = user.sub || user.id;

            // Update username and onboarding status (using /me endpoint)
            const usernameUpdateData = {
                id: userId,
                username: username.trim(),
                posthog_tracking_enabled: posthogTracking,
                posthog_opted_in_at: posthogTracking ? new Date().toISOString() : null,
            };

            // If this is onboarding, mark onboarding as complete
            if (isOnboarding) {
                usernameUpdateData.onboarding_complete = true;
            }

            const updatedUser = await apiClient.put('/api/users/me', usernameUpdateData);

            // For admin users, update roles separately using admin endpoint
            if (isAdmin) {
                // Determine new roles: always keep admin, add teacher or student based on selection
                const baseRoles = ['admin']; // Always keep admin role
                const teachingRole = isTeacher ? 'teacher' : 'student';
                const newRoles = [...baseRoles, teachingRole];

                const rolesData = {
                    roles: newRoles
                };

                // Update roles using admin endpoint
                await apiClient.put(`/api/users/${userId}/roles`, rolesData);
            }

            // Update localStorage and PostHog consent
            localStorage.setItem('posthog_tracking_enabled', posthogTracking.toString());
            if (posthogTracking) {
                posthog.opt_in_capturing();
            } else {
                posthog.opt_out_capturing();
            }

            setSuccess(true);
            setError(null);

            // Check if role changed or username changed - if so, refresh tokens
            const roleChanged = isAdmin && (originalIsTeacher !== isTeacher);
            const usernameChanged = originalUsername !== username.trim();
            const needsReauth = roleChanged || usernameChanged;

            // Set reauthentication flag if needed
            if (needsReauth) {
                setWillReauthenticate(true);
            }

            // Call callback if provided
            if (onUserUpdated) {
                // Create updated user object with new values
                const userWithUpdates = {
                    ...updatedUser,
                    onboarding_complete: isOnboarding ? true : updatedUser.onboarding_complete
                };
                onUserUpdated(userWithUpdates);
            }

            // If role or username changed, refresh tokens to get updated scopes/claims
            if (needsReauth) {
                try {
                    // Clear all cached tokens
                    await clearAllTokens();

                    // Re-authenticate with consent prompt to get fresh tokens with new roles/username
                    signIn({
                        redirectUri: window.location.origin + '/callback',
                        prompt: 'consent',
                    });
                    return; // Early return - user will be redirected
                } catch (tokenError) {
                    console.error('Failed to refresh tokens:', tokenError);
                    // Fallback: force page reload to clear state
                    setTimeout(() => window.location.reload(), 1500);
                }
            }

        } catch (err) {
            console.error('Error updating user profile:', err);
            setError(err.message || 'Failed to update profile. Please try again.');
            setWillReauthenticate(false);
        } finally {
            // Only set loading to false if we're not re-authenticating
            if (!needsReauth) {
                setLoading(false);
            }
        }
    };

    const handleUsernameChange = (e) => {
        setUsername(e.target.value);
        setError(null);
        setSuccess(false);
        setWillReauthenticate(false);
    };

    const handleRoleChange = (event, newRole) => {
        if (newRole !== null) {
            setIsTeacher(newRole === 'teacher');
            setSuccess(false);
            setWillReauthenticate(false);
        }
    };

    return (
        <Backdrop
            open={true}
            sx={{
                color: '#fff',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2
            }}
        >
            <Card sx={{
                maxWidth: 500,
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto'
            }}>
                <CardContent sx={{p: 4}}>
                    {showGreeting && (
                        <>
                            <Box sx={{textAlign: 'center', mb: 3}}>
                                <Typography variant="h4" gutterBottom color="primary">
                                    {title}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {isOnboarding
                                        ? "Let's set up your profile to get started."
                                        : "Update your profile information."
                                    }
                                </Typography>
                            </Box>
                            <Divider sx={{mb: 3}}/>
                        </>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Box sx={{mb: 3}}>
                            <Typography variant="h6" gutterBottom>
                                Choose Your Username
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                Your username will be visible to other users on the platform. Use letters, numbers, and
                                underscores only.
                            </Typography>
                            <TextField
                                fullWidth
                                label="Username"
                                value={username}
                                onChange={handleUsernameChange}
                                error={!!error && error.includes('username')}
                                helperText={error && error.includes('username') ? error : 'Must start with a letter or underscore. Can contain letters, numbers, and underscores.'}
                                required
                                disabled={loading}
                                placeholder="Enter your username"
                            />
                        </Box>

                        {/* Show role selector only for admin users */}
                        {isAdmin && (
                            <Box sx={{mb: 4}}>
                                <Typography variant="h6" gutterBottom>
                                    Select Your Teaching Role
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    Choose your primary teaching/learning role. You will keep your administrator
                                    privileges.
                                </Typography>

                                <ToggleButtonGroup
                                    value={isTeacher ? 'teacher' : 'student'}
                                    exclusive
                                    onChange={handleRoleChange}
                                    fullWidth
                                    disabled={loading}
                                    sx={{height: 100}}
                                >
                                    <ToggleButton
                                        value="student"
                                        sx={{
                                            flex: 1,
                                            flexDirection: 'column',
                                            gap: 1,
                                            textTransform: 'none',
                                            py: 2
                                        }}
                                    >
                                        <Person fontSize="large"/>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Typography variant="subtitle2" fontWeight="medium">
                                                Student
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Take courses
                                            </Typography>
                                        </Box>
                                    </ToggleButton>

                                    <ToggleButton
                                        value="teacher"
                                        sx={{
                                            flex: 1,
                                            flexDirection: 'column',
                                            gap: 1,
                                            textTransform: 'none',
                                            py: 2
                                        }}
                                    >
                                        <School fontSize="large"/>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Typography variant="subtitle2" fontWeight="medium">
                                                Teacher
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Create courses
                                            </Typography>
                                        </Box>
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                {/* Show admin status */}
                                <Box sx={{mt: 2, textAlign: 'center'}}>
                                    <Chip
                                        label="Administrator"
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                        sx={{fontWeight: 'medium'}}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{mt: 0.5}}>
                                        Your admin privileges will be preserved
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Show current roles for non-admin teachers only (not for students) */}
                        {!isAdmin && isTeacher && (
                            <Box sx={{mb: 4}}>
                                <Typography variant="h6" gutterBottom>
                                    Your Role
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                    Your role has been assigned by an administrator.
                                </Typography>
                                <Box sx={{textAlign: 'center'}}>
                                    <Chip
                                        label="Teacher"
                                        color="primary"
                                        size="medium"
                                        sx={{fontWeight: 'medium'}}
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{mt: 1}}>
                                        Contact an administrator to change your role
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Analytics Tracking Consent */}
                        <Box sx={{mb: 3}}>
                            <Divider sx={{mb: 3}}/>
                            <Typography variant="h6" gutterBottom>
                                Analytics & Privacy
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                Help us improve DuoQuanto by allowing us to collect anonymous usage data. This helps us
                                understand how you use the platform and make it better for everyone.
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={posthogTracking}
                                        onChange={(e) => setPosthogTracking(e.target.checked)}
                                        disabled={loading}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body2" fontWeight="medium">
                                            Enable analytics tracking
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            We collect anonymous usage statistics to improve the platform. No personal
                                            data is shared with third parties.
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>

                        {error && !error.includes('username') && (
                            <Alert severity="error" sx={{mb: 2}}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert severity="success" sx={{mb: 2}}>
                                {isOnboarding ? "Welcome to DuoQuanto! Your profile has been set up successfully!" : "Profile updated successfully!"}
                                {willReauthenticate ? " Refreshing session..." : ""}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading || !username.trim()}
                            sx={{mt: 2}}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{mr: 1}}/>
                                    {isOnboarding ? "Setting up..." : "Updating..."}
                                </>
                            ) : (
                                submitButtonText
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </Backdrop>
    );
};

export default UserSetup;